import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, take, zip } from 'rxjs';
import { DfMeDropdownComponent } from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import {
  buildDisplayedColumns,
  connectMatTableSort,
  getDstColStyle,
} from '../../shared/mat-table/dst-mat-grid.util';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { ReportsService } from '../reports.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';


import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
  selector: 'gtw-diagnostics-report',
  templateUrl: './diagnostics-report.component.html',
  styleUrls: [
    '../../shared/styles/dst-shared.scss',
    './diagnostics-report.component.scss',
    '../../shared/mat-table/dst-mat-table.scss',
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DiagnosticsReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  private contextApplied = false;

  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(MatSort) sort!: MatSort;

  data: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  cols: any[] = [
    {
      field: 'STATEMENTID',
      header: 'Statement Id',
      hidden: false,
      width: '145px'
    },
    {
      field: 'ATTACHMENTNAME',
      header: 'Attachment Name',
      hidden: false,
      width: '370px'
    },
    {
      field: 'ATTFORMTYPEDESC',
      header: 'Form Type',
      hidden: false,
      width: '145px'
    },
    {
      field: 'TAXYEAR',
      header: 'Tax Year',
      hidden: false,
      width: '100px'
    },
    {
      field: 'STATEMENTNAME',
      header: 'Statement Name',
      hidden: false,
      width: '300px'
    },
    {
      field: 'DCNNAME',
      header: 'DCN Name',
      hidden: false,
      width: '300px'
    },
    {
      field: 'PRIMARYBUSINESS',
      header: 'Transferee Business/ME',
      hidden: false,
      width: '300px'
    },
    {
      field: 'SECONDARYBUSINESS',
      header: 'Transferor Business/ME',
      hidden: false,
      width: '300px'
    },
    {
      field: 'PRIMARYLEID',
      header: 'Transferee (Primary LEID)',
      hidden: false,
      width: '155px'
    },
    {
      field: 'PRIMARYENTITY',
      header: 'Transferee Name',
      hidden: false,
      width: '300px'
    },
    {
      field: 'PRIMARYGEGECS',
      header: 'Primary GEGECS',
      hidden: false,
      width: '150px'
    },
    {
      field: 'SECONDARYGEGECS',
      header: 'Secondary GEGECS',
      hidden: false,
      width: '150px'
    },
    {
      field: 'CREATEDBY',
      header: 'User Activity',
      hidden: false,
      width: '400px'
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;
  selectedMes: string = '';
  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;

  getDstColStyle = getDstColStyle;

  constructor(private cDSerivce: CreateDisclosureService,
    private dstSharedService: DSTSharedService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerTabsService: TrackerTabsService,
    private dCRService: DSTDynamicComponentResolverService,
    private jsonExportService: JSONExportService,
    private reportsService: ReportsService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService) {

    this.excelExportFileName = 'Reports-Diagnostic_Report_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = buildDisplayedColumns(this.cols);
  }

  ngOnInit(): void {
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
    });

    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    connectMatTableSort(this.dataSource, this.sort);
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColByField(field: string): { field: string; header: string; hidden?: boolean; width?: string } {
    return this.cols.find((c) => c.field === field)!;
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;
      this.contextApplied = true;

      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);

      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
        this.taxYears = data[0];
        this.setTaxYear(taxYear);
        this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
        this.loadReport();
      });
    } catch (e) {
      this.dstSharedService.showAlert('error', 'Missing context, please contact system administrator for assistance!');
    }
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }
  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  // setScenarios(taxYear: string): void {
  //   this.scenarios = [ { text:'Select Scenario', value: null } ];

  //   const selectedTaxYear = this.taxYearScenarioData.values.find((item: any) => { return item.value.toString() === taxYear; });
  //   selectedTaxYear?.scenarios.filter((ele : any) => ele.is_locked === 'N' && ele.channel_key.toString() === this.selectedChannelKey).forEach((item : any) => {
  //     this.scenarios.push({
  //       text: item.scenario_desc,
  //       value: item.scenario,
  //       selected: item.selected
  //     });
  //   });
  //   this.scenario = this.scenarios.find((item: any) => { return item.value === selectedTaxYear?.scenario; });
  //   this.scenarioLoaded = true;
  // }
  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    console.log('Selected M/Es:- ' + this.selectedMes);
    this.reportsService.loadDiagnosticsReport(this.taxYear?.text.toString()!, this.selectedMes || '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.data = data;
      this.data?.forEach((element) => {
        element.statementId = element.STATEMENTID;
        element.taxYearId = element.TAXYEARID;
        element.formId = element.FORMID;
        element.formName = element.FORMNAME;
      });
      this.dataSource.data = this.data;
      connectMatTableSort(this.dataSource, this.sort);
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the N/A Report grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService.getChildStatements(rowData.STATEMENTID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
      this.showModalViewer = true;
      setTimeout(() => {
        this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
      });
    }, (eMsg: string) => {
      console.log(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      {
        field: 'STATEMENTID',
        header: 'Statement Id',
      },
      {
        field: 'ATTACHMENTNAME',
        header: 'Attachment Name',
      },
      {
        field: 'ATTFORMTYPEDESC',
        header: 'Form Type',
      },
      {
        field: 'TAXYEAR',
        header: 'Tax Year',
      },
      {
        field: 'STATEMENTNAME',
        header: 'Statement Name',
      },
      {
        field: 'DCNNAME',
        header: 'DCN Name',
      },
      {
        field: 'PRIMARYBUSINESS',
        header: 'Transferee Business/ME',
      },
      {
        field: 'SECONDARYBUSINESS',
        header: 'Transferor Business/ME',
      },
      {
        field: 'PRIMARYLEID',
        header: 'Transferee (Primary LEID)',
      },
      {
        field: 'PRIMARYENTITY',
        header: 'Transferee Name',
      },
      {
        field: 'PRIMARYGEGECS',
        header: 'Primary GEGECS',
      },
      {
        field: 'SECONDARYGEGECS',
        header: 'Secondary GEGECS',
      },
      {
        field: 'CREATEDBY',
        header: 'User Activity',
      }
    ];

    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, headersMap);
  }
}
